import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import BasicModalLayout from '../../../shared/components/modal/BasicModalLayout';
import { LCFormField } from '../../localcontent/shared/LCFormField';
import { dateFormatToISO, dateFormatToUTCMillis } from '../../../shared/utils/utils-string-to-date';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { StInput, StLabelWrapper, StSalesDate, StSalesDateLabel, StSettingsContainer } from './SettingsModal.styled';
import { usePreviousProps } from '../../../hooks/usePreviousProps';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';

export interface SettingsModalProps {
    visible: boolean;
    tooltips?: {
        campaignStart: string;
        campaignEnd: string;
        campaignTagLine: string | null;
        campaignExpiry?: string;
    };
    onClose: () => void;
    campaignStartDate?: string;
    campaignEndDate?: string;
    campaignTagLine?: string;
    minStartDateInMs: number;
    maxStartDateInMs: number;
    termLimit: number;
    salesStartDate?: number;
    updateProductCampaignStart: (campaignStart, campaignEnd, campaignTagLine, productId) => void;
    allowStartDateEditing: boolean;
    productId: string;
    allowExpiryEditing?: boolean;
}

const SettingsModal: FC<SettingsModalProps> = ({
    visible,
    tooltips = {
        campaignStart: i18n.t('marketing.yourCampaigns.campaignStartTooltip'),
        campaignEnd: i18n.t('marketing.yourCampaigns.campaignEndTooltip'),
        campaignTagLine: null
    },
    onClose,
    campaignStartDate,
    campaignEndDate,
    campaignTagLine,
    minStartDateInMs,
    maxStartDateInMs,
    termLimit,
    salesStartDate,
    updateProductCampaignStart,
    allowStartDateEditing,
    productId
}) => {
    const { t } = useTranslation();

    const campaignStart = campaignStartDate
        ? t('dateFormat.date', {
              val: new Date(campaignStartDate)
          })
        : '';
    const campaignEnd = campaignEndDate
        ? t('dateFormat.date', {
              val: new Date(campaignEndDate)
          })
        : '';
    const initialTagLine = campaignTagLine ? campaignTagLine : '';

    const [campaignStartStr, setCampaignStartStr] = useState(campaignStart);
    const [campaignEndStr, setCampaignEndStr] = useState(campaignEnd);
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [campaignTagLineValue, setCampaignTagLineValue] = useState(initialTagLine);

    const previousVisibleProp = usePreviousProps(visible);

    useEffect(() => {
        if (visible !== previousVisibleProp) {
            const campaignStartStr = campaignStartDate
                ? t('dateFormat.date', {
                      val: new Date(campaignStartDate)
                  })
                : '';
            const campaignEndStr = campaignEndDate
                ? t('dateFormat.date', {
                      val: new Date(campaignEndDate)
                  })
                : '';
            setCampaignStartStr(campaignStartStr);
            setCampaignEndStr(campaignEndStr);
            setStartDateError(false);
            setEndDateError(false);
        }
    }, [campaignEndDate, campaignStartDate, previousVisibleProp, visible, t]);

    const onChangeStartDate = useCallback(event => {
        setCampaignStartStr(event.target.value);
    }, []);

    const onChangeEndDate = useCallback(event => {
        setCampaignEndStr(event.target.value);
    }, []);

    const onChangeTagLine = useCallback(event => {
        setCampaignTagLineValue(event.target.value);
    }, []);

    const isValidInput = useCallback(() => {
        const startDateInMs = dateFormatToUTCMillis(campaignStartStr);
        const endDateInMs = dateFormatToUTCMillis(campaignEndStr);

        const validStartDate = startDateInMs! >= minStartDateInMs && startDateInMs! <= maxStartDateInMs;

        const startDateError = allowStartDateEditing && !validStartDate;

        const maxEndDate = startDateInMs! + termLimit * 24 * 3600 * 1000;

        const endDateError = startDateInMs! >= endDateInMs! || endDateInMs! >= maxEndDate;
        setEndDateError(endDateError);
        setStartDateError(startDateError);
        return !(endDateError || startDateError);
    }, [allowStartDateEditing, campaignEndStr, campaignStartStr, maxStartDateInMs, minStartDateInMs, termLimit]);

    const saveSettings = useCallback(() => {
        const inputValid = isValidInput();

        if (inputValid) {
            const campaignStart = dateFormatToISO(campaignStartStr);
            const campaignEnd = dateFormatToISO(campaignEndStr);

            updateProductCampaignStart(campaignStart, campaignEnd, campaignTagLineValue, productId);
            onClose();
        }
    }, [
        campaignEndStr,
        campaignStartStr,
        campaignTagLineValue,
        isValidInput,
        onClose,
        productId,
        updateProductCampaignStart
    ]);

    const header = <h2 style={{ fontSize: '22px' }}>{t('marketing.yourCampaigns.campaignSettings')}</h2>;
    const startDateProps = useMemo(
        () => ({
            type: 'text',
            placeholder: t('dateFormat.placeholder'),
            value: campaignStartStr,
            onChange: onChangeStartDate,
            disabled: false
        }),
        [campaignStartStr, onChangeStartDate, t]
    );

    const endDateProps = useMemo(
        () => ({
            type: 'text',
            placeholder: t('dateFormat.placeholder'),
            value: campaignEndStr,
            onChange: onChangeEndDate
        }),
        [campaignEndStr, onChangeEndDate, t]
    );

    const tagLineProps = useMemo(
        () => ({
            type: 'text',
            placeholder: t('marketing.yourCampaigns.none'),
            value: campaignTagLineValue,
            onChange: onChangeTagLine
        }),
        [campaignTagLineValue, onChangeTagLine, t]
    );

    startDateProps.disabled = !allowStartDateEditing;

    let salesDateLabel: ReactNode | null = null;
    if (salesStartDate) {
        const salesDateString = t('dateFormat.date', {
            val: new Date(salesStartDate)
        });
        salesDateLabel = (
            <StSalesDateLabel>
                {t('marketing.yourCampaigns.campaignTitleReleaseDate')}
                <br />
                <StSalesDate>{salesDateString}</StSalesDate>
            </StSalesDateLabel>
        );
    }
    const content = (
        <>
            <StSettingsContainer>
                <StLabelWrapper>
                    <LCFormField
                        title={t('marketing.yourCampaigns.campaignStart')}
                        isEdit={true}
                        tooltipText={tooltips.campaignStart}
                        editComponent={<StInput {...startDateProps} error={startDateError} />}
                    />
                    {salesDateLabel}
                </StLabelWrapper>
                <StLabelWrapper>
                    <LCFormField
                        title={t('marketing.yourCampaigns.campaignEnd')}
                        isEdit={true}
                        tooltipText={tooltips.campaignEnd}
                        editComponent={<StInput {...endDateProps} error={endDateError} />}
                    />
                </StLabelWrapper>
            </StSettingsContainer>
            <StSettingsContainer>
                <LCFormField
                    title={t('marketing.yourCampaigns.campaignTagLine')}
                    isEdit={true}
                    tooltipText={tooltips.campaignExpiry}
                    editComponent={<StInput {...tagLineProps} />}
                />
            </StSettingsContainer>
        </>
    );

    const cancelButton = (
        <Button onClick={onClose} text={t('cancel')} size={ButtonSize.LARGE} theme={ButtonTheme.NEUTRAL} />
    );

    const saveButton = <Button onClick={saveSettings} text={t('save')} size={ButtonSize.LARGE} />;

    return (
        <BasicModalLayout
            header={header}
            content={content}
            cancelButton={cancelButton}
            okButton={saveButton}
            visible={visible}
            width={380}
            onClose={onClose}
        />
    );
};

export default SettingsModal;
